import { type DocumentNode, gql } from '@apollo/client';

/**
 * Variables for createProfile mutation
 */
export interface CreateProfileVariables {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password1?: string;
  password2?: string;
}

/**
 * User structure in created profile
 */
export interface CreatedProfileUser {
  id: string;
  username: string;
  email: string;
}

/**
 * Media group node structure
 */
export interface CreateProfileMediaGroupNode {
  id: string;
  name: string;
}

/**
 * Media group edge structure
 */
export interface CreateProfileMediaGroupEdge {
  node: CreateProfileMediaGroupNode;
}

/**
 * Media groups connection structure
 */
export interface CreateProfileMediaGroupsConnection {
  edges: CreateProfileMediaGroupEdge[];
}

/**
 * Permissions group structure in created profile
 */
export interface CreateProfilePermissionsGroup {
  id: string;
  name: string;
  description: string | null;
  mediaGroups: CreateProfileMediaGroupsConnection;
}

/**
 * Media permission node in created profile
 */
export interface CreateProfileMediaPermissionNode {
  id: string;
  permissionsGroup: CreateProfilePermissionsGroup;
}

/**
 * Media permission edge in created profile
 */
export interface CreateProfileMediaPermissionEdge {
  node: CreateProfileMediaPermissionNode;
}

/**
 * Media permission set connection in created profile
 */
export interface CreateProfileMediaPermissionSetConnection {
  edges: CreateProfileMediaPermissionEdge[];
}

/**
 * Created profile structure
 */
export interface CreatedProfile {
  id: string;
  user: CreatedProfileUser;
  role: string | null;
  phoneNumber: string | null;
  companyName: string | null;
  thirdPartyEmail: string | null;
  pageSize: number | null;
  pageSizeApp: number | null;
  defaultTab: string | null;
  lastlogin: string | null;
  ip: string | null;
  lastPasswordChange: string | null;
  resumeEnabled: boolean;
  resumeUnder5: boolean;
  textOnly: boolean;
  dateGmtName: string | null;
  dateUtcOffset: string | null;
  expiration: string | null;
  authenticateWithCas: boolean;
  authenticateWithSaml: boolean;
  mediapermissionSet: CreateProfileMediaPermissionSetConnection;
}

/**
 * Create profile payload structure
 */
export interface CreateProfilePayload {
  profile: CreatedProfile;
}

/**
 * Response structure for createProfile mutation
 */
export interface CreateProfileResponse {
  createProfile: CreateProfilePayload;
}

const createProfile: DocumentNode = gql`
  mutation add_user(
    $username: String!
    $email: String!
    $firstName: String!
    $lastName: String!
    $password1: String
    $password2: String
  ) {
    createProfile(
      username: $username
      email: $email
      firstName: $firstName
      lastName: $lastName
      password1: $password1
      password2: $password2
    ) {
      profile {
        id
        user {
          id
          username
          email
        }
        role
        phoneNumber
        companyName
        thirdPartyEmail
        pageSize
        pageSizeApp
        defaultTab
        lastlogin
        ip
        lastPasswordChange
        resumeEnabled
        resumeUnder5
        textOnly
        dateGmtName
        dateUtcOffset
        expiration
        authenticateWithCas
        authenticateWithSaml
        mediapermissionSet {
          edges {
            node {
              id
              permissionsGroup {
                id
                name
                description
                mediaGroups {
                  edges {
                    node {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default createProfile;
